import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';

interface User {
  id: number;
  name: string;
  name_ar?: string;
  email: string;
  role: 'admin' | 'manager' | 'staff' | 'tenant' | 'technician';
  phone?: string;
  address?: string;
}

interface AuthContextType {
  user: User | null;
  roles: string[];
  permissions: string[];
  technicianId?: number | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  can: (key: string) => boolean;
}

// Registration removed

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [technicianId, setTechnicianId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authService.getCurrentUser()
        .then((data) => {
          // data: { user, roles, permissions }
          setUser(data.user);
          setRoles(Array.isArray(data.roles) ? data.roles : []);
          setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
          setTechnicianId(typeof data.provider_id === 'number' ? data.provider_id : (data.provider_id ? parseInt(data.provider_id) : null));
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);
      localStorage.setItem('token', response.token);
      setUser(response.user);
      setRoles(Array.isArray(response.roles) ? response.roles : []);
      setPermissions(Array.isArray(response.permissions) ? response.permissions : []);
      setTechnicianId(typeof response.provider_id === 'number' ? response.provider_id : (response.provider_id ? parseInt(response.provider_id) : null));
    } catch (error) {
      throw error;
    }
  };
  // Registration removed

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setRoles([]);
    setPermissions([]);
    setTechnicianId(null);
  };

  const can = (key: string) => {
    // Check if user is admin through legacy enum role or RBAC roles
    if (user?.role === 'admin') return true;
    if (Array.isArray(roles) && roles.some((r: string) => r.toLowerCase() === 'admin')) return true;
    // Check permissions (admins get '*' permission which grants all)
    return permissions.includes(key) || permissions.includes('*');
  };

  const value = {
    user,
    roles,
    permissions,
    technicianId,
    loading,
    login,
    logout,
    can,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};


